import * as THREE from 'three';
import { AdditiveBlending, Color, DoubleSide, Group, Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Scene, TextureLoader, type Camera, type WebGLRenderer } from 'three';
import type { IScene, SceneContext } from '../../engine/types';
import type { AssetLoader } from '../../engine/AssetLoader';
import { CameraSystem } from '../../engine/CameraSystem';
import { LightingRig } from '../../engine/LightingRig';
import { IcosahedronController } from './IcosahedronController';
import { BallInteraction } from './BallInteraction';
import { DeviceMotion } from './DeviceMotion';
import {
  glbPath,
  meshNames,
  cameraConfig,
  lights,
  environment,
  ballOrientation,
  ballScreenShift,
  cameraIntro,
  envFadeDuration,
} from './MagicBallConfig';
import { cubicBezier, type EasingFn } from '../../engine/easing';
import { EffectManager } from './effects/EffectManager';
import type { BallAppearance } from '../../api/types';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

const CAP_POSITION: [number, number, number] = [0.1114, 2.7087, 0.0450];
const ICO_POSITION: [number, number, number] = [0.0418, 1.3513, 0.0530];


export class MagicBallScene implements IScene {
  readonly name = 'magic-ball';

  private scene = new Scene();
  private _cameraSystem: CameraSystem | null = null;
  private lightingRig = new LightingRig();
  private gltf: GLTF | null = null;
  private icoMesh: Mesh | null = null;
  private icoGroup: Group | null = null;
  private idleMesh: Mesh | null = null;
  private _ballShellGroup: Group | null = null;
  private loader: AssetLoader | null = null;
  private _controller: IcosahedronController | null = null;
  private _interaction: BallInteraction | null = null;
  private _deviceMotion: DeviceMotion | null = null;
  private renderer: WebGLRenderer | null = null;

  private ballMat: MeshStandardMaterial | null = null;
  private windowMat: MeshStandardMaterial | null = null;
  private icoMat: MeshStandardMaterial | null = null;
  private originalIcoMap: THREE.Texture | null = null;
  private originalIcoEmissive: THREE.Texture | null = null;
  private generatedTexture: THREE.Texture | null = null;

  private introActive = false;
  private introElapsed = 0;
  private introStart = new THREE.Vector3();
  private introEnd = new THREE.Vector3();
  private introEase: EasingFn = cubicBezier(...cameraIntro.bezier);

  private envFadeActive = false;
  private envFadeElapsed = 0;
  private envFadeTargetBg = 1;
  private envFadeTargetEnv = 0.3;
  private envFadePending = false;
  private effects: EffectManager | null = null;

  getController(): IcosahedronController | null { return this._controller; }
  getInteraction(): BallInteraction | null { return this._interaction; }
  getCameraSystem(): CameraSystem | null { return this._cameraSystem; }
  getDeviceMotion(): DeviceMotion | null { return this._deviceMotion; }
  getLightingRig(): LightingRig { return this.lightingRig; }
  getLoader(): AssetLoader | null { return this.loader; }
  getRenderer(): WebGLRenderer | null { return this.renderer; }
  getBallShellGroup(): Group | null { return this._ballShellGroup; }
  getIcoGroup(): Group | null { return this.icoGroup; }
  getThreeScene(): Scene { return this.scene; }

  async load(loader: AssetLoader): Promise<void> {
    this.loader = loader;
    this.gltf = await loader.loadGLTF(glbPath);
  }

  async setup(context: SceneContext): Promise<void> {
    if (!this.gltf || !this.loader) return;
    this.renderer = context.renderer;

    const gltfScene = this.gltf.scene;
    let sharedMat: MeshStandardMaterial | null = null;
    let ballMesh: Mesh | null = null;
    let windowMesh: Mesh | null = null;
    let capMesh: Mesh | null = null;

    gltfScene.traverse((child) => {
      if (!(child instanceof Mesh)) return;

      if (child.name === meshNames.ball) {
        sharedMat = child.material as MeshStandardMaterial;
        ballMesh = child;

        this.ballMat = sharedMat.clone();
        this.ballMat.transparent = false;
        this.ballMat.depthWrite = true;
        this.ballMat.roughness = 0;
        // Solid color overrides the baked GLB texture
        this.ballMat.map = null;
        this.ballMat.color.set(0x0084ff);
        if ('clearcoat' in this.ballMat) {
          (this.ballMat as unknown as { clearcoat: number; clearcoatRoughness: number }).clearcoat = 1.0;
          (this.ballMat as unknown as { clearcoatRoughness: number }).clearcoatRoughness = 0.1;
        }
        child.material = this.ballMat;
      }

      if (child.name === meshNames.window) {
        windowMesh = child;
        this.windowMat = (child.material as MeshStandardMaterial).clone();
        this.windowMat.transparent = true;
        this.windowMat.opacity = 0.82;
        this.windowMat.depthWrite = false;
        this.windowMat.roughness = 0.1;
        child.material = this.windowMat;
        child.renderOrder = 2;

        const cap = child.clone();
        cap.rotateX(Math.PI);
        cap.material = new MeshStandardMaterial({
          color: 0x000000,
          side: DoubleSide,
          depthWrite: true,
        });
        cap.renderOrder = 0;
        cap.position.set(...CAP_POSITION);
        cap.scale.x *= 1.5;
        cap.scale.y *= 1.5;
        capMesh = cap;
      }

      if (child.name === meshNames.icosahedron) {
        this.icoMesh = child;
        child.renderOrder = 1;
      }
    });

    if (this.icoMesh && sharedMat) {
      this.icoMat = (sharedMat as MeshStandardMaterial).clone();
      this.icoMat.emissive.set(0x1ae4ff);
      this.icoMat.emissiveIntensity = 5.8;
      this.icoMat.transparent = true;
      this.icoMat.depthWrite = false;
      this.icoMat.blending = AdditiveBlending;
      // Skip ACES tone mapping on just this mesh so the bright cyan emissive
      // stays bright instead of desaturating into the filmic dark-blue rolloff.
      this.icoMat.toneMapped = false;
      this.icoMesh.material = this.icoMat;
      this.originalIcoMap = this.icoMat.map;
      this.originalIcoEmissive = this.icoMat.emissiveMap;
    }

    const rootGroup = new Group();
    rootGroup.name = 'ballRoot';
    rootGroup.quaternion.set(...ballOrientation);
    gltfScene.add(rootGroup);

    this._ballShellGroup = new Group();
    if (ballMesh) { (ballMesh as Mesh).removeFromParent(); this._ballShellGroup.add(ballMesh as Mesh); }
    if (windowMesh) { (windowMesh as Mesh).removeFromParent(); this._ballShellGroup.add(windowMesh as Mesh); }
    if (capMesh) this._ballShellGroup.add(capMesh as Mesh);

    // Idle-mode "?" plane is attached to the ball SHELL group (not the
    // icoGroup) so it inherits the same tilt + wobble rotations the ball
    // shell receives from `BallInteraction`. Otherwise the question mark
    // stays fixed in space while the ball rotates around it on shake.
    this.idleMesh = this.buildIdleMesh();
    this._ballShellGroup.add(this.idleMesh);

    rootGroup.add(this._ballShellGroup);

    if (this.icoMesh) {
      this.icoMesh.removeFromParent();
      this.icoGroup = new Group();
      this.icoGroup.position.set(...ICO_POSITION);
      this.icoMesh.position.set(0, 0, 0);
      this.icoGroup.add(this.icoMesh);

      rootGroup.add(this.icoGroup);
    }

    if (this.icoGroup && this._ballShellGroup) {
      this._controller = new IcosahedronController();
      this._controller.init(this.icoGroup, this._ballShellGroup);
    }


    this.scene.add(gltfScene);
    this._cameraSystem = new CameraSystem(cameraConfig, context.canvas);
    this.effects = new EffectManager(this.scene, this._cameraSystem.camera);
    this.effects.activate('nebula');

    // Horizontal framing: positive shifts the ball toward screen right (pan camera left)
    if (ballScreenShift !== 0) {
      const cam = this._cameraSystem.camera;
      const target = this._cameraSystem.controls.target;
      const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
      const pan = camRight.multiplyScalar(-ballScreenShift);
      cam.position.add(pan);
      target.add(pan);
      this._cameraSystem.controls.update();
    }

    // Camera fly-in: start far along the same view ray, settle at the configured position
    {
      const cam = this._cameraSystem.camera;
      const target = this._cameraSystem.controls.target;
      this.introEnd.copy(cam.position);
      this.introStart.copy(cam.position).sub(target).multiplyScalar(cameraIntro.startScale).add(target);
      cam.position.copy(this.introStart);
      cam.lookAt(target);
      this._cameraSystem.controls.enabled = false;
      this.introActive = true;
      this.introElapsed = 0;
    }

    if (this._ballShellGroup && ballMesh && this._cameraSystem) {
      this._interaction = new BallInteraction(
        this._ballShellGroup,
        ballMesh,
        this._cameraSystem.camera,
        context.canvas,
        this._cameraSystem.controls,
      );
      this._interaction.onShakeTriggered = () => {
        if (this._controller && !this._controller.isAnimating) {
          this._controller.showRandom();
        }
      };
    }

    this._deviceMotion = new DeviceMotion();
    this._deviceMotion.onShake = () => {
      if (this._controller && !this._controller.isAnimating) {
        this._controller.showRandom();
      }
    };
    this._deviceMotion.onSimulateShake = () => {
      this._interaction?.simulateShake();
    };
    if (!this._deviceMotion.needsPermission && this._deviceMotion.isSupported) {
      this._deviceMotion.start();
    }

    // Non-blocking: lights apply instantly inside setup(); the HDRI loads async
    // and fades in when ready, so first render isn't gated on a multi-MB download.
    this.scene.backgroundIntensity = 0;
    this.scene.environmentIntensity = 0;
    this.lightingRig
      .setup(this.scene, context.renderer, lights, environment, this.loader)
      .then(() => {
        if (this.introActive) {
          // hold the skybox until the camera fly-in lands
          this.envFadeTargetEnv = this.scene.environmentIntensity || environment.intensity || 1;
          this.scene.backgroundIntensity = 0;
          this.scene.environmentIntensity = 0;
          this.envFadePending = true;
        } else {
          this.startEnvFade();
        }
      });
  }

  private startEnvFade(): void {
    this.envFadeTargetBg = 1;
    this.envFadeTargetEnv = this.scene.environmentIntensity || environment.intensity || 1;
    this.scene.backgroundIntensity = 0;
    this.scene.environmentIntensity = 0;
    this.envFadeElapsed = 0;
    this.envFadeActive = true;
  }

  updateAppearance(options: BallAppearance): void {
    if (this.ballMat) {
      if (options.roughness !== undefined) this.ballMat.roughness = options.roughness;
      if (options.clearcoat !== undefined && 'clearcoat' in this.ballMat) {
        (this.ballMat as unknown as { clearcoat: number }).clearcoat = options.clearcoat;
      }
      if (options.clearcoatRoughness !== undefined && 'clearcoatRoughness' in this.ballMat) {
        (this.ballMat as unknown as { clearcoatRoughness: number }).clearcoatRoughness = options.clearcoatRoughness;
      }
      if (options.color !== undefined) {
        this.ballMat.color.set(options.color as number);
      }
    }
    if (this.icoMat) {
      if (options.emissiveIntensity !== undefined) this.icoMat.emissiveIntensity = options.emissiveIntensity;
      if (options.emissiveColor !== undefined) this.icoMat.emissive.set(options.emissiveColor as number);
    }
    if (this.windowMat) {
      if (options.windowOpacity !== undefined) this.windowMat.opacity = options.windowOpacity;
      if (options.windowTint !== undefined) this.windowMat.color.set(options.windowTint as number);
    }
  }

  private originalBallMap: THREE.Texture | null = null;

  getIcoSourceImage(): HTMLImageElement | ImageBitmap | null {
    const img = this.originalIcoMap?.image;
    return img ?? null;
  }

  applyMessageTextures(texture: THREE.Texture): void {
    if (!this.icoMat) return;
    this.generatedTexture?.dispose();
    this.generatedTexture = texture;
    this.icoMat.map = texture;
    this.icoMat.emissiveMap = texture;
    this.icoMat.needsUpdate = true;
  }

  restoreOriginalTextures(): void {
    if (!this.icoMat) return;
    this.generatedTexture?.dispose();
    this.generatedTexture = null;
    this.icoMat.map = this.originalIcoMap;
    this.icoMat.emissiveMap = this.originalIcoEmissive;
    this.icoMat.needsUpdate = true;
  }

  /// Toggles between "idle" mode (icosahedron hidden, red "?" mesh shown)
  /// and "answer" mode (icosahedron visible, mesh hidden). Used by the
  /// native shell to create the first-shake ritual.
  setIdleMode(idle: boolean): void {
    if (this.icoMesh)  this.icoMesh.visible  = !idle;
    if (this.idleMesh) this.idleMesh.visible =  idle;
  }

  /// Hard-toggle the icosahedron mesh visibility. Bypasses the sink/rise
  /// animation system — used by the shake flow to make the ico vanish on
  /// the exact frame a shake begins, and stay gone until the shake ends.
  setIcoVisibility(visible: boolean): void {
    if (this.icoMesh) this.icoMesh.visible = visible;
  }

  private buildIdleMesh(): Mesh {
    // Flat plane textured with the pre-rendered "?" PNG. Lives in world
    // space — i.e. NOT a billboard — so when the user orbits the ball with
    // a touch drag they see the plane from new angles (foreshortened from
    // the side, occluded by the ball shell when viewed from behind).
    //
    // We deliberately do NOT override `renderOrder` and we leave depth
    // testing on, so the ball geometry can occlude the plane normally
    // when the camera moves around to the back.
    const aspect = 265 / 427; // PNG width / height
    const height = 0.9;
    const width = height * aspect;
    const geometry = new PlaneGeometry(width, height);

    const texture = new TextureLoader().load('question.png');
    texture.anisotropy = 8;

    const material = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      // DoubleSide so orbiting around to the back of the plane still shows
      // the texture (instead of an invisible back face). The texture
      // appears mirrored from the back; that's the expected behaviour for
      // a sign rendered on both sides of a thin physical card.
      side: DoubleSide,
    });
    const mesh = new Mesh(geometry, material);
    // Position + rotation hand-tuned via the temporary debug panel. The
    // tuned values were in `icoGroup`-local space; converting to
    // `_ballShellGroup`-local (origin) means adding `ICO_POSITION` to the
    // position. Rotation stays the same since both groups share orientation.
    mesh.position.set(
      -0.100 + ICO_POSITION[0],
       0.700 + ICO_POSITION[1],
       0.000 + ICO_POSITION[2],
    );
    mesh.rotation.set(-1.485, 0.084, -2.601);
    mesh.visible = false; // `setIdleMode(true)` flips this on after init.
    return mesh;
  }

  updateBallColor(color: string | number): void {
    if (!this.ballMat) return;
    if (!this.originalBallMap && this.ballMat.map) {
      this.originalBallMap = this.ballMat.map;
    }
    this.ballMat.map = null;
    this.ballMat.color.set(color as number);
    this.ballMat.needsUpdate = true;
  }

  restoreBallTexture(): void {
    if (!this.ballMat || !this.originalBallMap) return;
    this.ballMat.map = this.originalBallMap;
    this.ballMat.color.setRGB(1, 1, 1);
    this.ballMat.needsUpdate = true;
  }

  async updateBallTexture(path: string): Promise<void> {
    if (!this.ballMat || !this.loader) return;
    const tex = await this.loader.loadTexture(path, 'srgb');
    this.ballMat.map = tex;
    this.ballMat.needsUpdate = true;
  }

  updateEmissiveColor(color: string | number): void {
    if (this.icoMat) this.icoMat.emissive.set(color as number);
  }

  updateWindowTint(color: string | number): void {
    if (this.windowMat) this.windowMat.color.set(color as number);
  }

  updateBackground(color: string | number | null): void {
    if (color === null) {
      this.scene.background = null;
    } else {
      this.scene.background = new Color(color as number);
    }
  }

  async swapEnvironment(url: string | null, intensity?: number, asBackground?: boolean, presetName?: string): Promise<void> {
    if (!this.renderer || !this.loader) return;
    await this.lightingRig.swapEnvironment(url, this.renderer, this.scene, this.loader, intensity, asBackground);
    if (url) this.startEnvFade();
    if (presetName !== undefined) {
      this.effects?.activate(presetName);
    }
  }

  update(delta: number, _elapsed: number): void {
    this.effects?.update(delta, _elapsed);

    if (this.introActive && this._cameraSystem) {
      this.introElapsed += delta;
      const t = Math.min(this.introElapsed / cameraIntro.duration, 1);
      const e = this.introEase(t);
      const cam = this._cameraSystem.camera;
      cam.position.lerpVectors(this.introStart, this.introEnd, e);
      cam.lookAt(this._cameraSystem.controls.target);

      if (t >= 1) {
        cam.position.copy(this.introEnd);
        this.introActive = false;
        this._cameraSystem.controls.enabled = true;
        this._cameraSystem.controls.update();

        if (this.envFadePending) {
          this.envFadePending = false;
          this.envFadeElapsed = 0;
          this.envFadeActive = true;
        }
      }
    } else if (this._cameraSystem) {
      // advanceTween returns true while a programmatic re-center / setCamera
      // animation is running, and itself calls controls.update(). When no
      // tween is active, fall through to the standard OrbitControls update
      // so user-driven orbit/zoom continues to work.
      const tweening = this._cameraSystem.advanceTween(delta * 1000);
      if (!tweening) {
        this._cameraSystem.update();
      }
    }

    if (this.envFadeActive) {
      this.envFadeElapsed += delta;
      const t = Math.min(this.envFadeElapsed / envFadeDuration, 1);
      const e = t * (2 - t); // easeOutQuad
      this.scene.backgroundIntensity = this.envFadeTargetBg * e;
      this.scene.environmentIntensity = this.envFadeTargetEnv * e;
      if (t >= 1) this.envFadeActive = false;
    }

    this._deviceMotion?.update(delta);

    if (this._deviceMotion && this._interaction) {
      this._interaction.applyDeviceTilt(this._deviceMotion.tiltX, this._deviceMotion.tiltY);
    }

    this._interaction?.update(delta);
    this._controller?.update(delta);
  }

  getScene(): Scene {
    return this.scene;
  }

  getCamera(): Camera {
    return this._cameraSystem!.camera;
  }

  dispose(): void {
    this.effects?.dispose();
    this._interaction?.dispose();
    this._deviceMotion?.dispose();
    this._cameraSystem?.dispose();
    this.lightingRig.dispose();
    this.scene.clear();
  }
}
